import { useCallback } from 'react';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { isCombinedError } from 'shared/utils/errors/combinedErrorUtils';
import { CoreError } from 'shared/utils/errors/CoreError';
import { Maybe } from 'shared/utils/types';
import { CombinedError } from 'urql';

export interface FormattedCombinedError {
  title: string;
  message: string;
  raw: CombinedError;
}

export const useCombinedErrorFormatters = () => {
  const { defaultTitle, defaultMessage, graphqlErrorsTitle } =
    useDefaultErrorStrings();

  return useCallback(
    (errors?: CombinedError | null): FormattedCombinedError | null => {
      if (!errors) {
        return null;
      }

      const { graphQLErrors } = errors;

      // if not a combined error, return null
      if (!graphQLErrors) {
        return null;
      }

      if (graphQLErrors.length > 0) {
        // if error from gql attempt to suface some information:
        const [first] = graphQLErrors;
        return {
          title: graphqlErrorsTitle,
          message: first.message,
          raw: errors,
        };
      }
      // if network error or unexpected state, return default error message
      return {
        title: defaultTitle,
        message: defaultMessage,
        raw: errors,
      };
    },
    [defaultTitle, defaultMessage, graphqlErrorsTitle],
  );
};

/**
 * Takes urql combined errors and attempts to pluck a useful title
 * and message for presentation to the user.
 */
export const useFormattedCombinedError = (
  errors?: CombinedError | null,
): FormattedCombinedError | null => {
  const combinedErrorFormatter = useCombinedErrorFormatters();
  return combinedErrorFormatter(errors);
};

export const useFormattedCombinedOrCoreError = (
  errors?: Maybe<CombinedError | CoreError>,
): Maybe<{ title: string; message: string }> => {
  const formattedCombinedError = useFormattedCombinedError(
    isCombinedError(errors) ? errors : null,
  );

  if (!errors) {
    return null;
  }

  if (isCombinedError(errors)) {
    return formattedCombinedError;
  }

  return errors;
};

export const asCoreError = (
  error: Maybe<FormattedCombinedError>,
): CoreError | null => {
  if (!error) {
    return null;
  }

  return { ...error, code: '0' };
};
